import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import {
  fetchIncidents,
  readSession,
  humanizeApiError,
  INCIDENT_SEVERITIES,
  INCIDENT_STATUSES,
  type IncidentSeverity,
  type IncidentStatus,
  type IncidentSummary,
} from '../lib/api.js';
// Sawyer 12 incident-wording-pet-notes (F3) — the owner's word for the log.
import { useTenantVocabulary } from '../lib/useTenantVocabulary.js';

type IncidentsProps = { config: ClientConfig };

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; incidents: IncidentSummary[] };

const SEVERITY_LABEL: Record<IncidentSeverity, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

// Severity → swatch. Inline styles (admin-app has no Tailwind); muted→alarm.
const SEVERITY_COLOR: Record<IncidentSeverity, string> = {
  low: '#5b6472',
  medium: '#b45309',
  high: '#c2410c',
  critical: '#b91c1c',
};

const STATUS_LABEL: Record<IncidentStatus, string> = {
  open: 'Open',
  in_review: 'In review',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};

const KIND_LABEL: Record<string, string> = {
  bite: 'Bite',
  injury: 'Injury',
  escape: 'Escape',
  illness: 'Illness',
  altercation: 'Altercation',
  property_damage: 'Property damage',
  in_heat: 'In heat',
  other: 'Other',
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
}

export function Incidents({ config }: IncidentsProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const navigate = useNavigate();
  const { incident: noun } = useTenantVocabulary(apiUrl);
  const [searchParams] = useSearchParams();
  // Deep-link from a dog record (DogCard → "Incidents"): scope to one dog.
  // dogName is passed through so the banner reads correctly even when the dog
  // has zero incidents (the common case — clicking through from a clean dog).
  const dogId = searchParams.get('dogId') ?? undefined;
  const dogNameParam = searchParams.get('dogName') ?? undefined;
  const [severity, setSeverity] = useState<IncidentSeverity | 'all'>('all');
  const [status, setStatus] = useState<IncidentStatus | 'all'>('all');
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const { token } = readSession();
    if (!token) {
      setState({ kind: 'error', message: 'Not authenticated' });
      return;
    }
    try {
      const res = await fetchIncidents(apiUrl, token, {
        severity: severity === 'all' ? undefined : severity,
        status: status === 'all' ? undefined : status,
        dogId,
      });
      setState({ kind: 'ready', incidents: res.incidents });
    } catch (err) {
      setState({
        kind: 'error',
        message: humanizeApiError(err instanceof Error ? err.message : null),
      });
    }
  }, [apiUrl, severity, status, dogId]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = severity !== 'all' || status !== 'all';
  // Dog name for the scoped-view banner: prefer the name passed in the
  // deep-link (always available, even for a dog with no incidents), then the
  // first row's name, then a generic fallback.
  const scopedDogName =
    dogNameParam ??
    (dogId && state.kind === 'ready' && state.incidents[0]
      ? state.incidents[0].dog_name
      : null);

  return (
    <section className="ferro-admin-page" aria-busy={state.kind === 'loading'}>
      <header className="ferro-detail__header">
        <h1 className="ferro-section__title">{noun.many}</h1>
        <button
          type="button"
          className="ferro-btn ferro-btn--primary"
          onClick={() => navigate('/incidents/new')}
        >
          {noun.newAction}
        </button>
      </header>
      <p className="ferro-muted">
        Safety &amp; liability log — bites, injuries, escapes, and follow-up.
      </p>

      {dogId ? (
        <p className="ferro-muted" role="status">
          Showing {noun.manyLower} for {scopedDogName ?? 'this dog'}.{' '}
          <Link to="/incidents">View all {noun.manyLower}</Link>
        </p>
      ) : null}

      <div
        className="ferro-segmented"
        role="tablist"
        aria-label="Filter by severity"
      >
        {(['all', ...INCIDENT_SEVERITIES] as const).map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={severity === s}
            className={`ferro-segmented__btn${severity === s ? ' ferro-segmented__btn--active' : ''}`}
            onClick={() => setSeverity(s)}
          >
            {s === 'all' ? 'All severities' : SEVERITY_LABEL[s]}
          </button>
        ))}
      </div>
      <div
        className="ferro-segmented"
        role="tablist"
        aria-label="Filter by status"
        style={{ marginTop: '0.5rem' }}
      >
        {(['all', ...INCIDENT_STATUSES] as const).map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={status === s}
            className={`ferro-segmented__btn${status === s ? ' ferro-segmented__btn--active' : ''}`}
            onClick={() => setStatus(s)}
          >
            {s === 'all' ? 'All statuses' : STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {state.kind === 'loading' ? (
        <p className="ferro-muted" role="status">
          Loading {noun.manyLower}…
        </p>
      ) : null}

      {state.kind === 'error' ? (
        <div className="ferro-dashboard__error" role="alert">
          <p>{state.message}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        state.incidents.length === 0 ? (
          <p className="ferro-muted">
            {noun.emptyList}{filtered ? ' for this filter' : ''}.
          </p>
        ) : (
          <ul className="ferro-table" aria-label={`${noun.one} list`}>
            {state.incidents.map((i) => (
              <li key={i.id} className="ferro-table__row">
                <Link className="ferro-table__link" to={`/incidents/${i.id}`}>
                  <div className="ferro-table__primary">
                    {KIND_LABEL[i.kind] ?? i.kind} ·{' '}
                    {i.dog_name ?? 'Unknown dog'}
                  </div>
                  <div className="ferro-table__meta">
                    {i.customer_name ?? '—'} · {formatDate(i.occurred_at)} ·{' '}
                    {STATUS_LABEL[i.status]}
                    {i.reporter_name ? ` · filed by ${i.reporter_name}` : ''}
                  </div>
                  <span
                    className="ferro-table__badge"
                    style={{
                      backgroundColor: SEVERITY_COLOR[i.severity],
                      color: '#fff',
                    }}
                  >
                    {SEVERITY_LABEL[i.severity]}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </section>
  );
}
